import { List } from 'immutable';
import Immutable from 'immutable';
import { get } from './get';

/**
 * Sorts an Array or Immutable List by a path to an object's sortBy property (stored in state).
 * Triggers either a numeric or alphabetical sort based on the sort type defined/passed in.
 * @example Sort by productMeta.discount.payOnTime in each product with a numeric sortType,
 * will sort by Pay on Time discounts ASC/DESC
 * @param {Object} sortingConfig     - Props from page wrapper that defines the current sorting arguments.
 * @param {Object} iterableObj - The array to perform the sort on
 * @returns {Array}        - Sorted array
 */
const sortArray = (sortingConfig, iterableObj) => {
    if (!List.isList(iterableObj)) {
        throw Error('Sort Methods: Product List is not in proper type');
    }
    // Converts the iterable objects to an Array.
    const productArray = [...iterableObj];
    switch (sortingConfig.get('sortType')) {
        case 'numeric':
            return _numericSort(sortingConfig, productArray);
        case 'alpha':
            return _alphaSort(sortingConfig, productArray);
        case 'boolean':
            return _booleanSort(sortingConfig, productArray);
        default:
            return productArray;
    }
};

/**
 * Multi-level sort: Premium → Excess → Product Name (alphabetically)
 * @param {Immutable.Map} sortingConfig - Contains sortCriteria array
 * @param {Immutable.List} resultQuotes - List of product quotes
 * @returns {Array} Sorted array of quotes
 */
const sortArrayMultiLevel = (sortingConfig, resultQuotes) => {
    const sortCriteria = sortingConfig.get('sortCriteria');

    if (!sortCriteria || !Immutable.List.isList(sortCriteria)) {
        return resultQuotes.toArray();
    }

    return resultQuotes.sort((a, b) => {
        for (let i = 0; i < sortCriteria.size; i++) {
            const criterion = sortCriteria.get(i);
            const field = criterion.get('field');
            const dir = criterion.get('dir');
            const type = criterion.get('type');

            // Get nested values using field path (e.g., 'productMeta.price.annualPremium')
            const aValue = a.getIn(field.split('.'));
            const bValue = b.getIn(field.split('.'));

            let comparison = 0;

            if (type === 'numeric') {
                const aNum = parseFloat(aValue) || 0;
                const bNum = parseFloat(bValue) || 0;
                comparison = aNum - bNum;
            } else if (type === 'alpha') {
                const aStr = (aValue || '').toString();
                const bStr = (bValue || '').toString();
                comparison = aStr.localeCompare(bStr);
            }

            // If values differ, return the comparison result
            if (comparison !== 0) {
                return dir === 'asc' ? comparison : -comparison;
            }

            // If equal, continue to next criterion (cascade)
        }

        return 0; // All criteria are equal
    }).toArray();
};

/**
 * Sort numerically by finding the left and right values on state.
 * @param {Object} sorting Props from page wrapper that defines the current sorting arguments.
 * @param {Array} array The array to perform the sort on.
 * @returns {*|Iterable<K, V>|Array.<T>} Sorted array
 * @private
 */
const _numericSort = (sorting, array) => {
    return array.sort((left, right) => {
        const leftValue = get(left, sorting.get('sortBy'));
        const rightValue = get(right, sorting.get('sortBy'));
        if (leftValue === undefined && rightValue === undefined) {
            throw Error(`Cannot sort by a state key that does not exist: ${sorting.get('sortBy')}`);
        }
        if (sorting.get('sortDir') === 'asc') {
            return leftValue - rightValue;
        }
        return rightValue - leftValue;
    });
};

/**
 *
 * @param {Object} sorting Props from page wrapper that defines the current sorting arguments.
 * @param {Array} array The array to perform the sort on.
 * @returns {Array} Sorted array
 * @private
 */
const _booleanSort = (sorting, array) => (
    array.filter((item) => {
        return get(item, sorting.get('sortBy'));
    })
);

/**
 *
 * @param {Object} sorting Props from page wrapper that defines the current sorting arguments.
 * @param {Array} array The array to perform the sort on.
 * @returns {*|Iterable<K, V>|Array.<T>} Sorted array
 * @private
 */
const _alphaSort = (sorting, array) => {
    const BEFORE = -1;
    const EQUAL = 0;
    const AFTER = 1;

    return array.sort((left, right) => {
        const leftValue = get(left, sorting.get('sortBy'));
        const rightValue = get(right, sorting.get('sortBy'));
        if (sorting.get('sortDir') === 'asc') {
            if (leftValue < rightValue) {
                return BEFORE;
            }
            if (leftValue > rightValue) {
                return AFTER;
            }
            return EQUAL;
        }
        if (leftValue < rightValue) {
            return AFTER;
        }
        if (leftValue > rightValue) {
            return BEFORE;
        }
        return EQUAL;
    });
};

export { sortArray , sortArrayMultiLevel };
